/**
 * YR Gift Widget v2 — Floating gift reminder
 * Yves Rocher Serbia
 *
 * Shows on all pages (configurable). Fetches /cart.js for totals.
 * Always visible (even empty cart) to motivate purchases.
 */

if (!customElements.get('yr-gift-widget')) {
  customElements.define('yr-gift-widget', class extends HTMLElement {

    constructor() {
      super();
      this._open = false;
      this._tiers = [];
      this._cartTotal = 0;
      this._prevUnlocked = new Set();
      this._GIFT_PROP = '_yr_gift';
    }

    connectedCallback() {
      /* Parse config from JSON script block */
      const configId = this.dataset.configId;
      if (configId) {
        try {
          const scriptEl = document.getElementById(configId);
          if (scriptEl) {
            const config = JSON.parse(scriptEl.textContent);
            this._tiers = config.tiers || [];
          }
        } catch (e) {
          console.error('[yr-gift-widget] config parse error:', e);
          return;
        }
      }

      if (this._tiers.length === 0) return;

      /* Convert thresholds to cents */
      this._tiers.forEach(t => {
        t.thresholdCents = t.threshold * 100;
      });

      this._moneyFormat = this.dataset.moneyFormat || '{{amount}} RSD';
      this._hideOnCart = this.dataset.hideOnCart === 'true';

      /* Hide on cart page? */
      if (this._hideOnCart && this._isCartPage()) {
        this.style.display = 'none';
        return;
      }

      /* Cache DOM */
      this._fab = this.querySelector('.yr-gw__fab');
      this._badge = this.querySelector('.yr-gw__fab-badge');
      this._panel = this.querySelector('.yr-gw__panel');
      this._body = this.querySelector('.yr-gw__panel-body');
      this._overlay = this.querySelector('.yr-gw__overlay');
      this._closeBtn = this.querySelector('.yr-gw__panel-close');

      /* On mobile, move panel & overlay to <body> so fixed positioning works */
      this._isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (this._isMobile) {
        document.body.appendChild(this._overlay);
        document.body.appendChild(this._panel);
      }

      this._bindEvents();
      this._fetchAndRender();

      /* Show with animation */
      this.style.display = '';
      requestAnimationFrame(() => {
        this.classList.add('yr-gw--visible');
      });
    }

    _bindEvents() {
      this._fab.addEventListener('click', () => this._toggle());

      /* Close button — delegate on panel for safety after reparenting */
      this._panel.addEventListener('click', (e) => {
        if (e.target.closest('.yr-gw__panel-close')) {
          this._close();
        }
      });

      /* Overlay click */
      this._overlay.addEventListener('click', () => this._close());

      /* Also add touchend for iOS — sometimes click doesn't fire on overlay */
      this._overlay.addEventListener('touchend', (e) => {
        e.preventDefault();
        this._close();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this._open) this._close();
      });

      document.addEventListener('cart:updated', () => this._fetchAndRender());
      this._interceptCartFetch();
    }

    _interceptCartFetch() {
      const self = this;
      const origFetch = window.fetch;
      window.fetch = function (...args) {
        const result = origFetch.apply(this, args);
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
        if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update')) {
          result.then(() => {
            setTimeout(() => self._fetchAndRender(), 350);
          }).catch(() => {});
        }
        return result;
      };
    }

    _toggle() {
      this._open ? this._close() : this._openPanel();
    }

    _openPanel() {
      this._open = true;
      this.classList.add('yr-gw--open');
      this._panel.classList.add('yr-gw--open-panel');
      this._overlay.classList.add('yr-gw--open-overlay');
      this._panel.setAttribute('aria-hidden', 'false');
      this._fetchAndRender();
    }

    _close() {
      this._open = false;
      this.classList.remove('yr-gw--open');
      this._panel.classList.remove('yr-gw--open-panel');
      this._overlay.classList.remove('yr-gw--open-overlay');
      this._panel.setAttribute('aria-hidden', 'true');
    }

    async _fetchAndRender() {
      try {
        const cart = await this._getCart();
        this._cartTotal = this._calcTotal(cart);
        this._renderPanel();
        this._updateBadge();
      } catch (err) {
        console.error('[yr-gift-widget] fetch error:', err);
      }
    }

    /* ── Render panel content ── */
    _renderPanel() {
      const total = this._cartTotal;
      const newUnlocked = new Set();
      let html = '';

      this._tiers.forEach(tier => {
        const unlocked = total >= tier.thresholdCents;
        if (unlocked) newUnlocked.add(tier.tier);

        const progress = tier.thresholdCents > 0
          ? Math.min(100, Math.round((total / tier.thresholdCents) * 100))
          : 0;
        const remaining = Math.max(0, tier.thresholdCents - total);
        const justUnlocked = unlocked && !this._prevUnlocked.has(tier.tier);
        const statusClass = unlocked ? 'yr-gw__tier--unlocked' : 'yr-gw__tier--locked';
        const glowClass = justUnlocked ? ' yr-gw__tier--just-unlocked' : '';

        const statusText = unlocked
          ? '<span style="color:#014e2e;font-weight:600;">✓ Poklon otključan!</span>'
          : total > 0
            ? 'Nedostaje još ' + this._formatMoney(remaining)
            : this._formatMoney(tier.thresholdCents) + ' za poklon';

        const icon = unlocked
          ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M20 6L9 17l-5-5"/></svg>'
          : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>';

        /* Gift thumbnails with labels */
        let thumbsHtml = '';
        if (tier.products && tier.products.length > 0) {
          if (tier.products.length === 1) {
            /* Single gift — horizontal: image left, name right */
            const p = tier.products[0];
            const imgHtml = p.img
              ? '<div class="yr-gw__gift-thumb"><img src="' + this._escapeHtml(p.img) + '" alt="' + this._escapeHtml(p.title) + '" loading="lazy" width="52" height="52"></div>'
              : '<div class="yr-gw__gift-thumb yr-gw__gift-thumb--empty"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/></svg></div>';
            thumbsHtml = '<div class="yr-gw__gift-single">' + imgHtml + '<span class="yr-gw__gift-name-h">' + this._escapeHtml(p.title) + '</span></div>';
          } else {
            /* Multiple gifts — row with labels below */
            thumbsHtml = '<div class="yr-gw__gifts">';
            tier.products.forEach(p => {
              thumbsHtml += '<div class="yr-gw__gift-item">';
              if (p.img) {
                thumbsHtml += '<div class="yr-gw__gift-thumb"><img src="' + this._escapeHtml(p.img) + '" alt="' + this._escapeHtml(p.title) + '" loading="lazy" width="52" height="52"></div>';
              } else {
                thumbsHtml += '<div class="yr-gw__gift-thumb yr-gw__gift-thumb--empty"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/></svg></div>';
              }
              thumbsHtml += '<span class="yr-gw__gift-name">' + this._escapeHtml(p.title) + '</span>';
              thumbsHtml += '</div>';
            });
            thumbsHtml += '</div>';
          }
        }

        html += '<div class="yr-gw__tier ' + statusClass + glowClass + '">' +
          '<div class="yr-gw__tier-top">' +
            '<div class="yr-gw__tier-icon">' + icon + '</div>' +
            '<div class="yr-gw__tier-text">' +
              '<span class="yr-gw__tier-label">' + this._escapeHtml(tier.label) + '</span>' +
              '<span class="yr-gw__tier-status">' + statusText + '</span>' +
            '</div>' +
          '</div>' +
          '<div class="yr-gw__progress"><div class="yr-gw__progress-fill" style="width:' + progress + '%"></div></div>' +
          thumbsHtml +
        '</div>';
      });

      /* Pulse FAB on new unlock */
      let newlyUnlocked = false;
      newUnlocked.forEach(t => {
        if (!this._prevUnlocked.has(t)) newlyUnlocked = true;
      });
      if (newlyUnlocked && this._fab) {
        this._fab.classList.remove('yr-gw__fab--pulse');
        void this._fab.offsetWidth;
        this._fab.classList.add('yr-gw__fab--pulse');
      }

      this._prevUnlocked = newUnlocked;
      this._body.innerHTML = html;
    }

    _updateBadge() {
      const total = this._cartTotal;
      let unlocked = 0;
      this._tiers.forEach(tier => {
        if (total >= tier.thresholdCents) unlocked++;
      });
      if (unlocked > 0) {
        this._badge.textContent = unlocked;
        this._badge.style.display = '';
      } else {
        this._badge.style.display = 'none';
      }
    }

    _isCartPage() {
      return /^\/cart(\/|$)/.test(window.location.pathname);
    }

    _calcTotal(cart) {
      let total = 0;
      (cart.items || []).forEach(item => {
        if (!item.properties || !item.properties[this._GIFT_PROP]) {
          total += item.final_line_price;
        }
      });
      return total;
    }

    _formatMoney(cents) {
      const amount = (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return this._moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', amount)
        .replace('{{amount_with_comma_separator}}', amount);
    }

    _escapeHtml(str) {
      const d = document.createElement('div');
      d.textContent = str || '';
      return d.innerHTML;
    }

    async _getCart() {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      return r.json();
    }
  });
}
